import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

const MIN_PASSWORD_LENGTH = 8;
const HAS_UPPERCASE = /[A-Z]/;
const HAS_NUMBER = /[0-9]/;
const HAS_SPECIAL_CHARACTER = /[^A-Za-z0-9]/;

function getPasswordStrength(password: string) {
  return (
    (password.length >= MIN_PASSWORD_LENGTH ? 1 : 0) +
    (HAS_UPPERCASE.test(password) ? 1 : 0) +
    (HAS_NUMBER.test(password) ? 1 : 0) +
    (HAS_SPECIAL_CHARACTER.test(password) ? 1 : 0)
  );
}

export function AccountSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signUp = useAuthStore((s) => s.signUp);

  const email = searchParams.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);
  const strengthLabel =
    passwordStrength <= 1 ? 'Weak' : passwordStrength <= 3 ? 'Moderate' : 'Strong';
  const strengthColor =
    passwordStrength <= 1 ? 'bg-loss' : passwordStrength <= 3 ? 'bg-brand' : 'bg-win';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('No email address found. Please use the link from your approval email.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password);
      navigate('/confirm-email', { state: { email, redirectTo: '/onboarding' } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Account setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-win/10 border border-win/25 mx-auto mb-4">
        <ShieldCheck className="w-6 h-6 text-win" />
      </div>

      <h2 className="text-xl font-semibold text-gray-100 text-center mb-1">Set up your account</h2>
      <p className="text-sm text-gray-500 text-center mb-6">
        Your access has been approved. Create a password to get started.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5">
            Email
          </label>
          <div className="w-full px-3 py-2 bg-surface-3 border border-surface-4 rounded-md text-sm text-gray-400 select-all">
            {email || <span className="text-gray-600 italic">No email found — use the link from your approval email</span>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="At least 8 characters"
          />
          {password.length > 0 && (
            <div className="mt-2">
              <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                <div
                  className={`h-full ${strengthColor} transition-all`}
                  style={{ width: `${(passwordStrength / 4) * 100}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Password strength: {strengthLabel}</p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="confirm">
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="Re-enter password"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShieldCheck className="w-4 h-4" />
          {loading ? 'Setting up…' : 'Create account'}
        </button>
      </form>
    </>
  );
}
